import type { OurRoles } from "../constants";
import { roles } from "../constants";
import type { MdyUser } from "../monday/users.types";

export const ROLE_PERMISSIONS = {
  [roles.SUPERUSER]: {
    configuration: ["read", "edit"],
    invoices: ["read", "create", "edit", "delete", "send", "sync", "force-sync"],
    estimates: ["read", "create", "edit", "delete", "send", "sync", "force-sync"],
    customers: ["read", "create", "edit", "delete", "sync", "force-sync"],
    products: ["read", "create", "edit", "sync"], // Added "sync" for standard users
  },
  [roles.OPERATIONS]: {
    configuration: ["read"],
    invoices: ["read", "create", "edit", "delete", "send", "sync", "force-sync"],
    estimates: ["read", "create", "edit", "delete", "send", "sync", "force-sync"],
    customers: ["read", "create", "edit", "delete", "sync", "force-sync"],
    products: ["read", "create", "edit", "sync"], // Added "sync" for standard users
  },
  [roles.STANDARD]: {
    invoices: ["read", "create", "edit", "sync", "send"], // Added "sync" for standard users
    estimates: ["read", "create", "edit", "sync", "send"], // Added "sync" for standard users
    customers: ["read", "create", "edit", "sync"], // Added "sync" for standard users
    products: ["read", "create", "edit", "sync"], // Added "sync" for standard users
  },
  [roles.READ_ONLY]: {
    invoices: ["read"],
    estimates: ["read"],
    customers: ["read"],
  },
};

export const ROLE_HIERARCHY = {
  [roles.SUPERUSER]: [roles.OPERATIONS, roles.STANDARD, roles.READ_ONLY],
  [roles.OPERATIONS]: [roles.STANDARD, roles.READ_ONLY],
  [roles.STANDARD]: [roles.READ_ONLY],
  [roles.READ_ONLY]: [],
};

export const chooseDefaultRole = (user: Partial<MdyUser>) => {
  if (user.is_admin) {
    return roles.SUPERUSER;
  }

  if (user.is_guest) {
    return roles.NO_ACCESS;
  }

  if (user.is_view_only) {
    return roles.NO_ACCESS;
  }

  // Default role for all other users
  return roles.NO_ACCESS;
};

export const hasPermission = (
  role: OurRoles | null, // Only allow valid roles
  feature: keyof (typeof ROLE_PERMISSIONS)[OurRoles], // Feature must be a valid key
  action: string,
): boolean => {
  if (!role || !(role in ROLE_PERMISSIONS)) return false;

  // Dynamically merge permissions for the role based on the hierarchy
  const mergedPermissions = mergePermissions(role, feature);

  // Check if the action is included in the merged permissions
  return mergedPermissions.includes(action);
};

// Helper function to merge permissions based on the hierarchy
const mergePermissions = (role: OurRoles, feature: keyof (typeof ROLE_PERMISSIONS)[OurRoles]): string[] => {
  // Start with the role's own permissions
  const permissions = new Set(ROLE_PERMISSIONS[role][feature] || []);

  // Recursively add permissions from lower roles
  for (const inheritedRole of ROLE_HIERARCHY[role]) {
    const inheritedPermissions = ROLE_PERMISSIONS[inheritedRole][feature] || [];
    inheritedPermissions.forEach((perm) => permissions.add(perm));
  }

  return Array.from(permissions);
};
